import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from '../../middleware/ThemeContext';
import NoData from '../../components/listing/NoData';
import { useGetDuplicateCriteriaQuery, useGetDuplicatesQuery } from '../../api/duplicateApiSlice';
import { Container, Title, TitleCount, TitleGroup, TitleWrapper } from '../../components/listing/AMSListing';
import StyledText from '../../components/StyledComponents/StyledText';
import SingleCriteriaResultBox from './SingleCriteriaResultBox';
import SingleCriteriaResultSkeleton from './SingleCriteriaResultSkeleton';
import IgnoreDuplicatePopup from './IgnoreDuplicatePopup';
import ArrowIcon from '../../assets/icons/ArrowIcon';
import { useNavigate } from 'react-router-dom';

interface paginationTypes {
    limit: number,
    page: number,
    total: number,
    totalPages: number
}

const DuplicateOrganizations = () => {
    const {theme} = useTheme();
    const navigate = useNavigate();
    const [dataKey, setDataKey] = useState(0);
    const [ignorePopup, setIgnorePopup] = useState(false);
    const noDataRef = useRef<HTMLDivElement>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [allDuplicates, setAllDuplicates] = useState<any[]>([]);
    const [totalDuplicates, setTotalDuplicates] = useState<number>(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [hasInitialized, setHasInitialized] = useState(false);

    const { data: getDuplicates, isLoading, isFetching, refetch, isSuccess, error} = useGetDuplicatesQuery({scope: 'organization', page: currentPage}
        , {
        refetchOnMountOrArgChange: true,
    }
    );
    const { data: getCriteria, isLoading: loadingCriteria, error: err } = useGetDuplicateCriteriaQuery('organization');

    const refetchOrganizations = () => {
        setCurrentPage(1);
        setAllDuplicates([]);
        refetch();
    };

    const duplicates = (getDuplicates?.duplicateOrganizations ?? []) as any[];
    const dupPagination = (getDuplicates?.pagination ?? {}) as paginationTypes;
    const criteria = getCriteria?.duplicateCriteria?.columns ?? [];

    useEffect(() => {
    if (getDuplicates?.duplicateOrganizations) {
        if (currentPage === 1) {
        setAllDuplicates(getDuplicates.duplicateOrganizations);
        setTotalDuplicates(getDuplicates.pagination.total ?? 0);
        } else {
        setAllDuplicates(prev => [...prev, ...getDuplicates.duplicateOrganizations]);
        }
    }
    }, [getDuplicates, currentPage]);

    useEffect(() => {
    if (!isLoading && !isFetching && currentPage === 1 && duplicates?.length > 0) {
        setHasInitialized(true);
    }
    }, [isLoading, isFetching, currentPage, duplicates]);

    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
    if (!hasInitialized || currentPage >= dupPagination.totalPages || isLoading || isFetching) return;

    const observer = new IntersectionObserver(
        (entries) => {
        if (entries[0].isIntersecting && !isFetching) {
            setCurrentPage(prev => prev + 1);
        }
        },
        { threshold: 1.0 }
    );

    if (sentinelRef.current) {
        observer.observe(sentinelRef.current);
    }

    return () => {
        if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
        }
        observer.disconnect();
    };
    }, [hasInitialized, currentPage, dupPagination?.totalPages, isFetching, isLoading]);
    
    if(!isLoading && !isFetching  && Array.isArray(allDuplicates) && allDuplicates.length === 0) {
        return (
            <div style={{ height: 'calc(100vh - 250px)' }}>
        <NoData 
            title='No Duplicates Found'
            subtitle='There is no duplicate organizations found against the selected criteria.'
            ref={noDataRef}
        />
        </div>
        )
    }
  return (
    <>
        <Container theme={theme} className='rounded-0'>
            <div className="d-flex justify-content-between mt-0">
                <div className="d-flex align-items-center gap-2">
                    <TitleGroup className='mb-1'>
                        <TitleWrapper style={{ alignItems: 'center' }}>
                            <ArrowIcon className="cursor-pointer" onClick={() => navigate(-1)} color={theme.CorporateTheme.colors.textSecondary} />
                            <Title theme={theme}>Merge Duplicate Organizations</Title>
                            <TitleCount theme={theme}>({totalDuplicates})</TitleCount>
                        </TitleWrapper>
                        <StyledText fontSize="14px" fontWeight={300} textColor={theme.CorporateTheme.colors.textSecondary}>
                            All duplicate organizations will show here
                        </StyledText>
                    </TitleGroup>
                </div>
            </div>
        </Container>
        <div className="container-fluid" style={{ backgroundColor: theme.CorporateTheme.colors.bannerBg, height: 'calc(100vh - 204px)', overflowY: 'auto' }} ref={scrollContainerRef}>
            <div className="row pt-4 px-2 pb-2">
                <div className="col-12">
                    <StyledText>Duplicate Results</StyledText>
                    <div className='mt-4 d-grid' style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', overflow: 'auto' }}>
                        {isLoading || (isFetching && currentPage === 1) ? 
                           Array.from({ length: 6 }).map((_, index) => (
                                <SingleCriteriaResultSkeleton key={index} />
                            ))
                        :
                            allDuplicates?.map((items, index) => (
                                <SingleCriteriaResultBox 
                                    key={index}
                                    openPopup={() => setIgnorePopup(true)} 
                                    data={items}
                                    criteria={criteria}
                                    index={index}
                                    setDataKey={(key) => setDataKey(key)}
                                    scope='ORGANIZATION'
                                />
                            ))    
                        }

                        {(isFetching || (currentPage < (dupPagination?.totalPages ?? 1))) && (
                            <div ref={sentinelRef} style={{ gridColumn: '1 / -1' }}>
                            {isFetching && currentPage > 1 && <div className="text-center">Loading more...</div>}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        {ignorePopup &&
            <IgnoreDuplicatePopup 
                data={allDuplicates[dataKey]}
                scope='organization'
                close={() => setIgnorePopup(false)} 
                refetch={() => refetchOrganizations()}
            />
        }
    </>
  )
}

export default DuplicateOrganizations